import React from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {MAX_IMG_SIZE} from '../../../constants';
import {uploadImageFile} from '../../../service/files.service';
import {formatBytes, randomString} from '../../../utils/utils';
import {FileResult} from '../BaseFileLoader';

type LoadedImage = {id: string; imageSrc: string; width: string; height: string; widthHeightRatio: number} | null;

export default function useImgFileReader(
    onLoad: (imgs: {id: string; imageSrc: string; width: string; height: string; widthHeightRatio: number}[]) => void,
    setError?: React.Dispatch<React.SetStateAction<boolean>>
): (files: FileResult[]) => void {
    const {t} = useTranslation();
    return async (files: FileResult[]) => {
        Promise.all<LoadedImage>(
            files.map(({file}) => {
                return new Promise((resolve) => {
                    if (!file || file.size > MAX_IMG_SIZE) {
                        if (setError) {
                            setError(true);
                        }
                        toast.error(
                            <>
                                <p>{t('presEditPage.editSidebar.validation.imageSize')}</p>
                                <p>
                                    {t('presEditPage.editSidebar.validation.maxSize')} {formatBytes(MAX_IMG_SIZE, 1, true)}
                                </p>
                            </>
                        );
                        resolve(null);
                        return;
                    }
                    if (setError) {
                        setError(false);
                    }

                    const image = new Image();
                    const objectUrlApi = window.URL || window.webkitURL;
                    const objectUrl = objectUrlApi.createObjectURL(file);
                    image.src = objectUrl;

                    image.onload = () => {
                        const widthHeightRatio = image.width / image.height;
                        objectUrlApi.revokeObjectURL(objectUrl);

                        uploadImageFile(file)
                            .then(({url}) => {
                                resolve({
                                    id: randomString(10),
                                    imageSrc: url,
                                    width: '11%',
                                    height: 'auto',
                                    widthHeightRatio,
                                });
                            })
                            .catch(() => {
                                toast.error(t('presEditPage.editSidebar.validation.imageSize'));
                                resolve(null);
                            });
                    };

                    image.onerror = () => {
                        objectUrlApi.revokeObjectURL(objectUrl);
                        resolve(null);
                    };
                });
            })
        ).then((res) => {
            const validFiles = res.filter((f): f is NonNullable<typeof f> => f != null);
            if (validFiles.length === 0) return;
            onLoad(validFiles);
        });
    };
}
